"use strict";

const _remove = require("lodash/remove");
import {Households} from "./Households";

export class Household {

    static createHousehold(households, hId) {
        return new Household(households, hId);
    }

    public _households: Households;
    private _hId: string;

    constructor(households, hId) {
        this._households = households;
        this._hId = hId;
    }

    get id() {
        return this._hId;
    }

    toString() {
        return `Household: ${this.title || this.id}`;
    }

    _findMembers(people) {
        return people.getKeys()
            .map(pKey => people.getPersonAtKey(pKey))
            .filter(person => (person.getHouseholdId() == this.id || person.getPersonKey() == this.id));
    }

    findMembers(people) {
        const cacheKey = `household.findMembers(${this.id})`;
        let cachedMembersKeys = people.getCachedValue(cacheKey);
        if (typeof cachedMembersKeys != "undefined") {
            return cachedMembersKeys.map(k => people.getPersonAtKey(k));
        }
        const cachedMembers = this._findMembers(people);
        //if we save the array people may mutate it!
        cachedMembersKeys = cachedMembers.map(p => p.getPersonKey());
        people.setCachedValue(cacheKey, cachedMembersKeys);
        return cachedMembers;
    }

    getHouseholdAddresseeForPerson(people, p) {
        const members = this.findMembers(people);

        // want the main recipient (regardless if they're the HoH or +1)'s name to be first
        if (p) {
            _remove(members, function (m) {
                return m._personKey === p.getPersonKey();
            });
        }
        let householdFirstNames = members.map(m => m.firstName);
        if (p) {
            householdFirstNames.unshift(p.firstName);
        }

        const blanks = _remove(householdFirstNames, (name) => !name);
        if (blanks.length >= 1) {
            householdFirstNames.push((blanks.length === 1 ? "Guest" : "Guests"));
        }

        const namesString = [householdFirstNames.slice(0, -1).join(", "), householdFirstNames.slice(-1)[0]].join(householdFirstNames.length < 2 ? "" : " & ");
        return namesString;
    }

    get _h() {
        return this._households._hGetRawHouseholds(this._hId) || {};
    }

    get _hDefault() {
        return {};
    }

    get title() {
        return this._h.title;
    }

    headOfHousehold(people) {
        const members = this.findMembers(people);
        for(let i = 0; i < members.length; ++i) {
            if (members[i].getIsHeadOfHousehold()) return members[i];
        }
        if(members.length) return members.pop();
        throw new Error("Nobody is in this household");
    }

    elevateToWritable(people) {
        return Promise.resolve()
          .then(() => {
              const p = this.headOfHousehold(people);
              return Promise.all([
                  p.setHouseholdId(this._hId),
                  p.setIsHeadOfHousehold(true)
              ]);
          })
          .then(() => new WritableHousehold(this, this._hId));
    }

}

export class WritableHousehold extends Household {

    addPlusOne(people, options) {
        return people._createPlusOne(this.id, options);
    }

    _updateProperty(propertyName, newPropertyValue, callback) {
        const defValue = this._hDefault[propertyName];

        /**
         *
         * There are 3 cases here.
         * 1. Write newValue
         * 2. Write null
         * 3. Keep what is in the database - we will still write (null/newValue) in this case.
         *
         * So there are really just 2 outcomes
         * 1. Write newValue
         * 2. Write null
         */
        let newValue = newPropertyValue;
        if (defValue == newValue) {
            newValue = null;
        }

        return this._households._hWriteRawHouseholdProperty(this.id, propertyName, newValue, callback);
    }

    updateTitle(newTitle, callback) {
        return this._updateProperty("title", newTitle, callback);
    }

    elevateToWritable() {
        return Promise.resolve(this);
    }

}

(Household as any).Writable = WritableHousehold;
